import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiTruck,
  FiPlus,
  FiSearch,
  FiGrid,
  FiList,
  FiEye,
  FiEdit3,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiPhone,
  FiMail,
  FiMapPin,
  FiX
} from "react-icons/fi";
import { FaTruck, FaCity } from "react-icons/fa";
import axios from "axios";
import Swal from "sweetalert2";

const DistributorPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [distributorList, setDistributorList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [viewMode, setViewMode] = useState("table"); // "table" (list) or "grid"
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  const fetchDistributor = async () => {
    setLoading(true);
    if (!token) return;

    const params = new URLSearchParams({
      per_page: perPage,
      page: currentPage,
      search: globalFilter,
    }).toString();

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/distributor?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resData = response.data;
      if (resData.success) {
        const rawList = Array.isArray(resData.data) ? resData.data : [];
        setDistributorList(rawList);
        setTotalPages(resData.meta?.last_page || 1);
        setTotalData(resData.meta?.total || rawList.length);
      }
    } catch (error) {
      console.error("Gagal mengambil data distributor:", error);
      setDistributorList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributor();
  }, [currentPage, globalFilter]);

  const handleDetail = (distributor) => {
    setSelectedDistributor(distributor);
  };

  const closeDetail = () => {
    setSelectedDistributor(null);
  };

  const handleEdit = (id) => {
    navigate(`/enterprise/distributor/edit-distributor/${id}`);
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: `Hapus Distributor "${name}"?`,
      text: "Data distributor ini akan dihapus secara permanen!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/v1/distributor/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 || response.status === 204) {
        await Swal.fire({
          title: "Terhapus!",
          text: `Distributor "${name}" berhasil dihapus.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        if (selectedDistributor?.id === id) {
          setSelectedDistributor(null);
        }

        fetchDistributor();
      }
    } catch (error) {
      console.error("Error delete distributor:", error);
      Swal.fire({
        title: "Gagal Menghapus",
        text: error.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Distributor
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiTruck className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Mitra PBF Aktif
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {loading ? "..." : distributorList.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FaTruck className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Cakupan Kota
            </p>
            <h2 className="text-3xl font-extrabold text-amber-600">
              {loading
                ? "..."
                : new Set(distributorList.map((d) => d.kota || d.city).filter(Boolean)).size}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-amber-50 text-amber-600 border border-amber-100">
            <FaCity className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & ACTION BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search */}
          <div className="relative w-full md:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari distributor, kota, atau email..."
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* View Mode Toggle */}
            <div className="flex items-center p-1 bg-gray-100 rounded-2xl border border-gray-200/80">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiGrid className="w-3.5 h-3.5" />
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "table"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiList className="w-3.5 h-3.5" />
                <span>Tabel</span>
              </button>
            </div>

            {/* Create Distributor CTA (GREEN BUTTON) */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/enterprise/distributor/create-distributor")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
            >
              <FiPlus className="w-4 h-4" />
              <span>Tambah Distributor</span>
            </motion.button>
          </div>
        </div>

        {/* 3. CONTENT AREA (GRID VS TABLE) */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : distributorList.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FiTruck className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada data distributor</p>
            <p className="text-xs text-gray-400 mt-1">
              Klik tombol 'Tambah Distributor' untuk mendaftarkan mitra PBF baru.
            </p>
          </div>
        ) : viewMode === "grid" ? (
          /* GRID CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {distributorList.map((item) => {
              const name = item.name || "Distributor";
              const city = item.kota || item.city || "Kota";
              const phone = item.telepon || item.phone || "-";
              const email = item.email || "-";
              const address = item.alamat || item.address || "-";

              return (
                <motion.div
                  key={item.id}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xl shadow-gray-200/40 flex flex-col justify-between space-y-4 hover:border-emerald-200 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 font-extrabold flex items-center justify-center text-sm shadow-sm flex-shrink-0">
                          <FiTruck className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base leading-tight">
                            {name}
                          </h3>
                          <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1 mt-0.5">
                            <FiMapPin className="w-3 h-3" />
                            {city}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 text-xs text-gray-600 border-t border-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Telepon</span>
                        <span className="font-mono font-semibold text-gray-800">{phone}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Email</span>
                        <span className="text-gray-700 truncate max-w-[160px]">{email}</span>
                      </div>
                      <div className="flex items-start justify-between">
                        <span className="text-gray-400">Alamat</span>
                        <span className="text-gray-700 text-right truncate max-w-[160px]">
                          {address}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleDetail(item)}
                      className="flex-1 py-2 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                    >
                      <FiEye className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </button>
                    <button
                      onClick={() => handleEdit(item.id)}
                      className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all"
                      title="Edit"
                    >
                      <FiEdit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, name)}
                      className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all"
                      title="Hapus"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Distributor</th>
                  <th className="p-3.5">Kota</th>
                  <th className="p-3.5">Telepon</th>
                  <th className="p-3.5">Email</th>
                  <th className="p-3.5">Alamat</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {distributorList.map((item, idx) => {
                  const name = item.name || "Distributor";
                  const city = item.kota || item.city || "-";
                  const phone = item.telepon || item.phone || "-";
                  const email = item.email || "-";
                  const address = item.alamat || item.address || "-";

                  return (
                    <tr key={item.id} className="hover:bg-emerald-50/30 transition-colors">
                      <td className="p-3.5 text-center font-semibold text-gray-400">
                        {(currentPage - 1) * perPage + idx + 1}
                      </td>
                      <td className="p-3.5 font-bold text-gray-900 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center text-xs flex-shrink-0">
                          <FiTruck className="w-4 h-4" />
                        </div>
                        <span>{name}</span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">{city}</td>
                      <td className="p-3.5 font-mono text-gray-600">{phone}</td>
                      <td className="p-3.5 text-gray-600">{email}</td>
                      <td className="p-3.5 text-gray-600 truncate max-w-[200px]">{address}</td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleDetail(item)}
                            className="p-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-all"
                            title="Detail"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(item.id)}
                            className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all"
                            title="Edit"
                          >
                            <FiEdit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, name)}
                            className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all"
                            title="Hapus"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION CONTROLS */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{distributorList.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> distributor
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {currentPage} dari {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* 4. SIDE DETAIL SLIDE-OVER DRAWER */}
      <AnimatePresence>
        {selectedDistributor && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDetail}
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs z-40"
            />

            {/* Slide Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 p-6 overflow-y-auto flex flex-col justify-between border-l border-gray-100"
            >
              <div className="space-y-6">
                {/* Header Drawer */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white font-extrabold flex items-center justify-center shadow-lg shadow-emerald-600/20">
                      <FiTruck className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Detail Distributor</h2>
                      <span className="text-xs text-gray-400">ID: #{selectedDistributor.id}</span>
                    </div>
                  </div>
                  <button
                    onClick={closeDetail}
                    className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Profile Header */}
                <div className="p-4 rounded-3xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 space-y-2">
                  <h3 className="text-xl font-extrabold text-gray-900">
                    {selectedDistributor.name}
                  </h3>
                  <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                    <FiMapPin className="w-3.5 h-3.5" />
                    {selectedDistributor.kota || selectedDistributor.city || "Kota Tidak Diisi"}
                  </p>
                </div>

                {/* Specs List */}
                <div className="space-y-3 text-xs">
                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiPhone className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Nomor Telepon / HP</p>
                      <p className="font-mono font-bold text-gray-800">
                        {selectedDistributor.telepon || selectedDistributor.phone || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiMail className="text-emerald-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Alamat Email</p>
                      <p className="font-bold text-gray-800">
                        {selectedDistributor.email || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                    <p className="text-gray-400 font-medium mb-1">Alamat Kantor / Gudang</p>
                    <p className="font-semibold text-gray-800 leading-relaxed">
                      {selectedDistributor.alamat || selectedDistributor.address || "Alamat tidak diisi"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Drawer Footer Edit Button */}
              <div className="pt-6 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(selectedDistributor.id)}
                  className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <FiEdit3 className="w-4 h-4" />
                  <span>Edit Data Distributor</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DistributorPage;
